import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useMemberAuth } from "./MemberAuthContext";
import { Wallet, CheckCircle2, Clock, Minus, AlertCircle, ArrowRight, Landmark } from "lucide-react";
import { MONTH_ABBR, MONTH_FULL, BankTransferBlock } from "./quota/parts";
import { QuotaBillingForm } from "./quota/QuotaBillingForm";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Inline quota status + pay flow. Two-step UX:
 *   pending → click "Preencher dados" → fill billing form → click "Confirmar e pagar"
 */
export default function MemberQuotaCard() {
  const { member } = useMemberAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState(false);
  const [step, setStep] = useState("idle"); // idle | billing
  const [bankDetails, setBankDetails] = useState(null);
  const [showBank, setShowBank] = useState(false);
  const [bill, setBill] = useState({
    issue_to: "personal",
    billing_name: "",
    billing_nif: "",
    billing_address: "",
    billing_company: "",
    billing_company_nif: "",
  });

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/members/quota/status`, { withCredentials: true });
        setData(r.data);
      } catch (e) {
        if (e?.response?.status !== 401) console.warn("[QuotaCard] /quota/status:", e?.message);
      } finally { setLoading(false); }
    })();
    // Fetch bank details once
    axios.get(`${API}/payments/bank-details`)
      .then((r) => setBankDetails(r.data))
      .catch((e) => console.warn("[QuotaCard] /bank-details:", e?.message));
  }, []);

  // Prefill from member profile when entering billing step
  useEffect(() => {
    if (step === "billing" && member) {
      setBill((b) => ({
        ...b,
        billing_name: b.billing_name || member.name || "",
        billing_company: b.billing_company || member.company || "",
      }));
    }
  }, [step, member]);

  const setModality = async (modality) => {
    try {
      const r = await axios.post(`${API}/members/quota/modality`, { modality }, { withCredentials: true });
      setData(r.data);
      toast.success(modality === "monthly" ? "Modalidade mensal ativada." : "Modalidade anual ativada.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro a alterar a modalidade.");
    }
  };

  if (loading) {
    return (
      <div className="bg-white border border-[hsl(var(--border))] p-7" data-testid="member-quota-card-loading">
        <Wallet className="h-6 w-6 text-[hsl(var(--accent))]" />
        <p className="mt-5 text-sm text-[hsl(var(--muted-foreground))]">A carregar quota…</p>
      </div>
    );
  }
  if (!data) return null;

  const { state, year, tier_label, amount_eur, receipt_number, modality, months_paid, months_due, monthly_price } = data;
  const isMonthlyView = modality === "monthly" && Array.isArray(months_paid);
  // Próximo mês a pagar: primeiro em dívida; senão, primeiro mês futuro não pago
  const nextDue = isMonthlyView
    ? (months_due?.[0] || [...Array(12)].map((_, i) => i + 1).find((m) => !months_paid.includes(m)) || null)
    : null;
  const payMonth = isMonthlyView ? nextDue : null;

  const submitPay = async (e) => {
    e?.preventDefault?.();
    if (bill.issue_to === "company" && !bill.billing_company.trim()) {
      toast.error("Indique o nome da empresa.");
      return;
    }
    setPaying(true);
    try {
      const r = await axios.post(
        `${API}/members/quota/checkout`,
        { ...bill, origin_url: window.location.origin, ...(payMonth ? { month: payMonth } : {}) },
        { withCredentials: true },
      );
      if (r.data.url) window.location.href = r.data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro a iniciar pagamento.");
      setPaying(false);
    }
  };

  if (state === "no_tier") {
    return (
      <div className="bg-white border border-[hsl(var(--border))] p-7" data-testid="member-quota-card-no-tier">
        <AlertCircle className="h-6 w-6 text-[hsl(var(--muted-foreground))]" />
        <h3 className="mt-5 display-heading text-2xl">Quota anual {year}</h3>
        <p className="mt-3 text-sm text-[hsl(var(--muted-foreground))] leading-relaxed">
          O seu nível de sócio ainda não foi atribuído pela Direção. Será notificado(a) por email quando estiver disponível.
        </p>
      </div>
    );
  }

  if (state === "exempt") {
    return (
      <div className="bg-white border border-[hsl(var(--border))] p-7" data-testid="member-quota-card-exempt">
        <Minus className="h-6 w-6 text-[hsl(var(--accent))]" />
        <div className="mt-5 flex items-center gap-2">
          <h3 className="display-heading text-2xl">Quota {year}</h3>
          <span className="text-xs px-2 py-1 bg-blue-50 text-blue-700 rounded-sm">Isenta</span>
        </div>
        <p className="mt-3 text-sm text-[hsl(var(--muted-foreground))] leading-relaxed">
          Como <strong>{tier_label}</strong>, está isento(a) de quota anual.
        </p>
      </div>
    );
  }

  if (state === "paid" && !isMonthlyView) {
    return (
      <div className="bg-white border border-emerald-200 p-7" data-testid="member-quota-card-paid">
        <CheckCircle2 className="h-6 w-6 text-emerald-600" />
        <div className="mt-5 flex items-center gap-2">
          <h3 className="display-heading text-2xl">Quota {year}</h3>
          <span className="text-xs px-2 py-1 bg-emerald-100 text-emerald-800 rounded-sm">Paga</span>
        </div>
        <p className="mt-3 text-sm text-[hsl(var(--muted-foreground))] leading-relaxed">
          {tier_label} · {Number(amount_eur).toFixed(2)} €{receipt_number ? ` · Declaração Nº ${String(receipt_number).padStart(4, "0")}` : ""}
        </p>
        <p className="mt-2 text-xs text-[hsl(var(--muted-foreground))]">Obrigado pelo seu apoio.</p>
      </div>
    );
  }

  const bankReference = isMonthlyView
    ? `Mensalidade ${payMonth ? MONTH_FULL[payMonth - 1] : ""} ${year} · ${member?.name || member?.email || ""}`
    : `Quota ${year} · ${member?.name || member?.email || ""}`;

  // pending — step 1: CTA / step 2: billing form
  return (
    <div className="bg-[hsl(var(--primary))] text-white p-7" data-testid="member-quota-card-pending">
      <Wallet className="h-6 w-6 text-[hsl(var(--accent))]" />
      <div className="mt-5 flex items-center gap-2">
        <h3 className="display-heading text-2xl">Quota {year}</h3>
        {isMonthlyView && state === "paid" ? (
          <span className="text-xs px-2 py-1 bg-emerald-100 text-emerald-800 rounded-sm inline-flex items-center gap-1">
            <CheckCircle2 className="h-3 w-3" /> Em dia
          </span>
        ) : (
          <span className="text-xs px-2 py-1 bg-amber-300 text-amber-900 rounded-sm inline-flex items-center gap-1">
            <Clock className="h-3 w-3" /> Pendente
          </span>
        )}
      </div>

      {/* Modalidade: anual vs mensal */}
      <div className="mt-4 flex items-center gap-2" data-testid="quota-modality-toggle">
        <span className="text-[10px] uppercase tracking-[0.18em] text-white/60">Modalidade:</span>
        <button type="button" onClick={() => modality !== "annual" && setModality("annual")}
          className={`px-3 py-1.5 text-[11px] uppercase tracking-[0.14em] border transition-colors ${modality !== "monthly" ? "bg-[hsl(var(--accent))] text-[#1A201F] border-[hsl(var(--accent))]" : "text-white/75 border-white/30 hover:border-white/60"}`}
          data-testid="quota-modality-annual">
          Anual
        </button>
        <button type="button" onClick={() => modality !== "monthly" && setModality("monthly")}
          className={`px-3 py-1.5 text-[11px] uppercase tracking-[0.14em] border transition-colors ${modality === "monthly" ? "bg-[hsl(var(--accent))] text-[#1A201F] border-[hsl(var(--accent))]" : "text-white/75 border-white/30 hover:border-white/60"}`}
          data-testid="quota-modality-monthly">
          Mensal {monthly_price ? `(${Number(monthly_price).toFixed(0)}€/mês)` : ""}
        </button>
      </div>

      {isMonthlyView ? (
        <>
          <p className="mt-4 text-sm text-white/85 leading-relaxed">
            Como <strong>{tier_label}</strong>, paga <strong>{Number(monthly_price).toFixed(2)} €/mês</strong>.
          </p>
          <div className="mt-3 grid grid-cols-6 sm:grid-cols-12 gap-1.5" data-testid="quota-month-grid">
            {MONTH_ABBR.map((ab, i) => {
              const mNum = i + 1;
              const paidM = months_paid.includes(mNum);
              const dueM = (months_due || []).includes(mNum);
              return (
                <div key={ab} data-testid={`quota-month-${mNum}`}
                  className={`text-center text-[10px] uppercase tracking-wide py-1.5 border ${
                    paidM ? "bg-emerald-500/25 border-emerald-300/50 text-emerald-100"
                      : dueM ? "bg-amber-400/25 border-amber-300/60 text-amber-100"
                        : "border-white/15 text-white/45"}`}>
                  {ab}{paidM ? " ✓" : ""}
                </div>
              );
            })}
          </div>
        </>
      ) : (
        <p className="mt-3 text-sm text-white/85 leading-relaxed">
          Como <strong>{tier_label}</strong>, a sua quota anual é de <strong>{Number(amount_eur).toFixed(2)} €</strong>.
        </p>
      )}

      {step === "idle" ? (
        <>
          {(!isMonthlyView || payMonth) && (
            <button
              onClick={() => setStep("billing")}
              className="mt-6 inline-flex items-center gap-2 px-5 py-3 bg-[hsl(var(--accent))] text-[#1A201F] text-xs uppercase tracking-[0.2em] font-semibold hover:bg-[#B3922E] transition-colors"
              data-testid="member-pay-quota-button"
            >
              {isMonthlyView ? `Pagar mensalidade de ${MONTH_FULL[payMonth - 1]}` : `Pagar quota ${year}`} <ArrowRight className="h-3.5 w-3.5" />
            </button>
          )}
          <p className="mt-3 text-[11px] text-white/65">Pagamento seguro Stripe (cartão, MB WAY ou Multibanco) · Recibo PDF enviado por email</p>

          <button
            type="button"
            onClick={() => setShowBank((v) => !v)}
            className="mt-5 inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-white/80 hover:text-white border-b border-white/30 hover:border-white pb-0.5"
            data-testid="member-bank-toggle"
          >
            <Landmark className="h-3.5 w-3.5" /> {showBank ? "Esconder dados de transferência" : "Prefere transferência bancária?"}
          </button>
          {showBank && bankDetails && (
            <BankTransferBlock
              bankDetails={bankDetails}
              reference={bankReference}
              isMonthlyView={isMonthlyView}
              monthlyPrice={monthly_price}
            />
          )}
        </>
      ) : (
        <QuotaBillingForm
          bill={bill}
          setBill={setBill}
          paying={paying}
          amountEur={amount_eur}
          onSubmit={submitPay}
          onCancel={() => setStep("idle")}
        />
      )}
    </div>
  );
}
